import React, { useState, useEffect } from 'react';
import { Modal, Form, InputNumber, Alert, Space, Typography, Divider } from 'antd';
import { WarningOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { UsageData } from '../../services/usageService';

const { Text, Title } = Typography;

interface LimitSettingModalProps {
  visible: boolean;
  usage: UsageData | null;
  onSave: (limit: number) => Promise<void>;
  onCancel: () => void;
}

const LimitSettingModal: React.FC<LimitSettingModalProps> = ({
  visible,
  usage,
  onSave,
  onCancel,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [limit, setLimit] = useState<number>(0);

  useEffect(() => {
    if (usage) {
      const initialLimit = usage.customHardLimit || usage.baseQuota * 2;
      setLimit(initialLimit);
      form.setFieldsValue({ limit: initialLimit });
    }
  }, [usage, form]);

  const handleOk = async () => {
    try {
      await form.validateFields();
      setLoading(true);
      await onSave(limit);
      setLoading(false);
      form.resetFields();
    } catch (error) {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  if (!usage) return null;

  const willBlockAt = limit;
  const currentUsage = usage.usageCount;
  const remainingUntilBlock = Math.max(0, willBlockAt - currentUsage);

  return (
    <Modal
      title={`Kullanım Limiti Ayarla - ${usage.featureName}`}
      open={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      confirmLoading={loading}
      okText="Kaydet"
      cancelText="İptal"
      width={600}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Current Plan Info */}
        <div
          style={{
            background: '#f5f5f5',
            padding: 16,
            borderRadius: 8,
          }}
        >
          <Title level={5}>Mevcut Plan Bilgileri</Title>
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text type="secondary">Temel Kota:</Text>
              <Text strong>{usage.baseQuota} kullanım</Text>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text type="secondary">Bu Ay Kullanılan:</Text>
              <Text strong>{usage.usageCount} kullanım</Text>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text type="secondary">Temel Fiyat:</Text>
              <Text strong>
                {usage.pricing?.basePrice} {usage.pricing?.currency}
              </Text>
            </div>
            {usage.pricing && usage.pricing.overagePrice > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text type="secondary">Aşım Ücreti:</Text>
                <Text strong>
                  {usage.pricing.overagePrice} {usage.pricing.currency} / {usage.pricing.overageUnit} kullanım
                </Text>
              </div>
            )}
          </Space>
        </div>

        <Divider />

        {/* Limit Setting Form */}
        <Form form={form} layout="vertical">
          <Form.Item
            label="Maksimum Kullanım Limiti"
            name="limit"
            rules={[
              { required: true, message: 'Lütfen limit değeri girin' },
              {
                type: 'number',
                min: currentUsage,
                message: `Limit mevcut kullanımdan (${currentUsage}) düşük olamaz`,
              },
            ]}
            extra={
              <Space direction="vertical" size="small" style={{ marginTop: 8 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  <InfoCircleOutlined /> Bu limite ulaşıldığında yeni istekler engellenecektir.
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Önerilen minimum: {usage.baseQuota * 1.5} (Temel kotanın %150'si)
                </Text>
              </Space>
            }
          >
            <InputNumber
              style={{ width: '100%' }}
              min={currentUsage}
              step={100}
              value={limit}
              onChange={(value) => setLimit(value || 0)}
              formatter={(value) => `${value} kullanım`}
              parser={(value) => parseInt(value?.replace(/\D/g, '') || '0', 10)}
            />
          </Form.Item>
        </Form>

        {/* Warning Alert */}
        {limit > 0 && (
          <Alert
            message="Limit Uyarısı"
            description={
              <Space direction="vertical" size="small">
                <Text>
                  Belirlediğiniz limite ({limit} kullanım) ulaşıldığında:
                </Text>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  <li>Yeni API istekleri <Text strong>429 (Too Many Requests)</Text> hatası ile reddedilecek</li>
                  <li>Mevcut kullanım: <Text strong>{currentUsage}</Text></li>
                  <li>Kalan hak: <Text strong type={remainingUntilBlock <= 100 ? 'danger' : 'success'}>
                    {remainingUntilBlock} kullanım
                  </Text></li>
                </ul>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Not: Temel kotayı ({usage.baseQuota}) aştığınızda ek ücretlendirme uygulanır ancak
                  istekler bloklanmaz. Sadece belirlediğiniz limitin üstü bloklanır.
                </Text>
              </Space>
            }
            type="warning"
            icon={<WarningOutlined />}
            showIcon
          />
        )}

        {/* Info Alert */}
        <Alert
          message="Bilgi"
          description="Limiti herhangi bir zamanda değiştirebilir veya kaldırabilirsiniz. Limit ayarlamak isteğe bağlıdır."
          type="info"
          showIcon
        />
      </Space>
    </Modal>
  );
};

export default LimitSettingModal;

